import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import type { DocsLink } from '../../types';
import { isRenderableDocsUrl } from '../../utils/docsLinks';

interface DocsLinkChipProps {
  /** The entity's docs. Absent/empty renders nothing at all. */
  links: DocsLink[] | undefined;
  /**
   * Whose docs these are, e.g. "Sellita" — used in the chip's tooltip and the
   * popover heading so a screen with two chips (brand + manufacturer) says which
   * is which.
   */
  entityLabel?: string;
  /** Extra classes on the outer wrapper (spacing is the caller's business). */
  className?: string;
  /** Hint shown under the popover heading, e.g. the tier-naming nudge. */
  hint?: string;
}

/**
 * "Docs" chip for an entity's {@link DocsLink} array.
 *
 * Three states, because one link should not cost a click:
 *   - no links   → renders nothing (the caller doesn't need its own guard)
 *   - one link   → the chip IS the anchor
 *   - many links → the chip opens a popover listing them
 *
 * Every destination goes through {@link isRenderableDocsUrl} before it becomes
 * an `href`. Entries that fail render as muted text with their URL, so a
 * mistyped link is visible and fixable rather than silently missing.
 */
export function DocsLinkChip({
  links,
  entityLabel,
  className,
  hint,
}: DocsLinkChipProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Dismiss on outside click / Escape. Capture phase for the key handler so a
  // chip inside a Modal closes the popover rather than the whole modal.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  if (!links || links.length === 0) return null;

  const chipClasses =
    'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ' +
    'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 ' +
    'focus:outline-none focus:ring-2 focus:ring-blue-300';
  const suffix = entityLabel ? ` for ${entityLabel}` : '';

  if (links.length === 1) {
    const only = links[0];
    if (!isRenderableDocsUrl(only.url)) {
      return (
        <span
          className={clsx('text-xs text-gray-500 italic', className)}
          title={only.url}
        >
          Docs{suffix}: {only.title} (no valid link)
        </span>
      );
    }
    return (
      <a
        href={only.url}
        target='_blank'
        rel='noopener noreferrer'
        className={clsx(chipClasses, className)}
        title={`${only.title}${suffix} — opens in a new tab`}
      >
        <span aria-hidden='true'>📄</span>
        {only.title}
      </a>
    );
  }

  return (
    <div ref={wrapperRef} className={clsx('relative inline-block', className)}>
      <button
        type='button'
        onClick={() => setOpen((v) => !v)}
        className={chipClasses}
        aria-expanded={open}
        aria-haspopup='true'
        title={`${links.length} documentation pages${suffix}`}
      >
        <span aria-hidden='true'>📄</span>
        Docs ({links.length})
      </button>
      {open && (
        <div className='absolute z-20 mt-1 w-72 rounded-md border border-gray-200 bg-white p-2 shadow-lg'>
          <p className='px-1 pb-1 text-xs font-semibold text-gray-500'>
            Documentation{suffix}
          </p>
          {hint && <p className='px-1 pb-1 text-xs text-gray-500'>{hint}</p>}
          <ul className='space-y-0.5'>
            {links.map((link, i) => (
              <li key={`${link.url}-${i}`}>
                {isRenderableDocsUrl(link.url) ? (
                  <a
                    href={link.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    onClick={() => setOpen(false)}
                    className='block truncate rounded px-1 py-1 text-xs text-blue-700 hover:bg-blue-50'
                    title={link.url}
                  >
                    {link.title}
                  </a>
                ) : (
                  <span
                    className='block truncate px-1 py-1 text-xs italic text-gray-500'
                    title={link.url}
                  >
                    {link.title} (no valid link)
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
